import axios from 'axios';
import { orders, inventory } from '@/networking/urlmanager';

const API_TIMEOUT = 15000; // 15 saniye

/**
 * Fetch orders with filtering options
 * @param {string} token - Authentication token
 * @param {string} search - Search term
 * @param {number} page - Page number
 * @param {number} isError - Error filter (0: show errors, 1: show valid)
 * @returns {Promise<Object>} - The order data
 */
export const fetchOrders = async (token, search = '', page = 0, isError = 1,filterCenter=null) => {
    try {
        const filterParam = Array.isArray(filterCenter) && filterCenter.length > 0
            ? filterCenter.map(r => r.name).join(',')
            : '';

        const response = await axios.get(orders.getAll, {
            params: {
                search,
                page,
                isError,
                filterCenter: filterParam
            },
            headers: {
                Authorization: `Bearer ${token}`
            },
            timeout: API_TIMEOUT
        });

        return response.data.data;
    } catch (error) {
        console.error('Error fetching orders:', error);
        throw error;
    }
};

/**
 * Save planned orders with vehicle assignments
 * @param {string} token - Authentication token
 * @param {Array} orderList - List of orders with vehicle assignments
 * @returns {Promise<Object>} - The response data
 */
export const saveOrdersWithVehicles = async (token, orderList) => {
    try {
        const response = await axios.post(
            orders.applyVehicle,
            { orderList },
            {
                headers: {
                    Authorization: `Bearer ${token}`
                },
                timeout: API_TIMEOUT
            }
        );

        return response.data;
    } catch (error) {
        console.error('Error saving orders with vehicles:', error);
        throw error;
    }
};

/**
 * Get order details by ID
 * @param {string} token - Authentication token
 * @param {string} orderId - Order ID
 * @returns {Promise<Object>} - The order details
 */
export const getOrderDetails = async (token, orderId) => {
    try {
        const response = await axios.get(`${orders.getById}/${orderId}`, {
            headers: {
                Authorization: `Bearer ${token}`
            },
            timeout: API_TIMEOUT
        });

        return response.data.data;
    } catch (error) {
        console.error('Error fetching order details:', error);
        throw error;
    }
};

/**
 * Add a new order
 * @param {string} token - Authentication token
 * @param {Object} orderData - Order data
 * @returns {Promise<Object>} - The created order
 */
export const createOrder = async (token, orderData) => {
    try {
        const response = await axios.post(
            orders.create,
            orderData,
            {
                headers: {
                    Authorization: `Bearer ${token}`
                },
                timeout: API_TIMEOUT
            }
        );

        return response.data;
    } catch (error) {
        console.error('Error creating order:', error);
        throw error;
    }
};

/**
 * Update an existing order
 * @param {string} token - Authentication token
 * @param {string} orderId - Order ID
 * @param {Object} orderData - Updated order data
 * @returns {Promise<Object>} - The updated order
 */
export const updateOrder = async (token, orderId, orderData) => {
    try {
        const response = await axios.put(
            `${orders.update}/${orderId}`,
            orderData,
            {
                headers: {
                    Authorization: `Bearer ${token}`
                },
                timeout: API_TIMEOUT
            }
        );

        return response.data;
    } catch (error) {
        console.error('Error updating order:', error);
        throw error;
    }
};

/**
 * Delete an order
 * @param {string} token - Authentication token
 * @param {string} orderId - Order ID
 * @returns {Promise<Object>} - The response data
 */
export const deleteOrder = async (token, orderId) => {
    try {
        const response = await axios.delete(`${orders.delete}/${orderId}`, {
            headers: {
                Authorization: `Bearer ${token}`
            },
            timeout: API_TIMEOUT
        });

        return response.data;
    } catch (error) {
        console.error('Error deleting order:', error);
        throw error;
    }
};

/**
 * Transfer equipment to a location or vehicle
 * @param {string} token - Authentication token
 * @param {number} equipmentId - Equipment ID
 * @param {string} transferTo - Transfer destination (VEHICLE_id, DEPOT, GARAGE, BRANCH)
 * @returns {Promise<Object>} - The response data
 */
export const transferEquipment = async (token, equipmentId, transferTo) => {
    try {
        let destination = {};

        if (transferTo.startsWith('VEHICLE_')) {
            const vehicleId = transferTo.replace('VEHICLE_', '');
            destination = {
                location_type: 'VEHICLE',
                vehicle_id: vehicleId
            };
        } else {
            destination = {
                location_type: transferTo
            };
        }

        const response = await axios.post(
            inventory.transfer,
            {
                inventory_id: equipmentId,
                ...destination
            },
            {
                headers: {
                    Authorization: `Bearer ${token}`
                },
                timeout: API_TIMEOUT
            }
        );

        return response.data;
    } catch (error) {
        console.error('Error transferring equipment:', error);
        throw error;
    }
};

/**
 * Plan order with equipment transfers
 * @param {string} token - Authentication token
 * @param {Object} orderData - Order data with vehicle assignment
 * @param {Array} equipmentTransfers - Array of equipment transfer objects
 * @returns {Promise<Object>} - The response data
 */
export const planOrderWithEquipment = async (token, orderData, equipmentTransfers) => {
    try {
        // Önce ekipman transferlerini gerçekleştir
        const transferPromises = equipmentTransfers.map(transfer =>
            transferEquipment(token, transfer.equipment_id, transfer.transfer_to)
        );

        await Promise.all(transferPromises);

        // Sonra siparişi planla
        const response = await axios.post(
            orders.applyVehicle,
            {
                orderList: [orderData]
            },
            {
                headers: {
                    Authorization: `Bearer ${token}`
                },
                timeout: API_TIMEOUT
            }
        );

        return response.data;
    } catch (error) {
        console.error('Error planning order with equipment:', error);
        throw error;
    }
};